/* istanbul ignore file */
import React, { useEffect } from 'react';
import type { ConnectedProps } from 'react-redux';
import { batch, connect } from 'react-redux';
import type { CookieObj } from '@noths/polaris-client-gdpr-compliance';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import * as recommendations from '@noths/polaris-client-recommendations';
import { grid, maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { visuallyHidden } from '@noths/polaris-client-styles';
import {
  actions as userConfigurationActions,
  getOrSetLoop54UserId,
  getOrSetRichRelevanceSessionId,
  getUserAttributes,
} from '@noths/polaris-client-user-configuration';
import { addRUMTiming, generateRandomString } from '@noths/polaris-client-utils';
import { type CurrencyCode, StatusCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { parse } from 'url';

import { BackToTopButton } from 'src/components/molecules/BackToTopButton/BackToTopButton';
import { ListingsApiErrorGuardContainer } from 'src/components/molecules/ListingsApiErrorGuard/ListingsApiErrorGuardContainer';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { SearchHeader } from 'src/components/molecules/SearchHeader/SearchHeader';
import { FilterContainer } from 'src/components/organisms/Filter/containers/FilterContainer';
import { QuickFiltersContainer } from 'src/components/organisms/Filter/containers/QuickFiltersContainer';
import { SlimFilterMenuContainer } from 'src/components/organisms/FilterMenu/containers/SlimFilterMenuContainer';
import { NoSearchResultsGuardContainer } from 'src/components/organisms/NoSearchResultsGuard/containers/NoSearchResultsGuardContainer';
import { ProductPagesContainer } from 'src/components/organisms/ProductPages/containers/ProductPagesContainer';
import { RecommendedProductsShelfContainer } from 'src/components/organisms/RecommendedProductsShelf/containers/RecommendedProductsShelfContainer';
import * as elementIds from 'src/constants/elementIds';
import { Placement } from 'src/constants/recommendations';
import { isProdLike } from 'src/environment';
import { useFilterNavType } from 'src/hooks/useFilterNavType/useFilterNavType';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { ProductsState } from 'src/redux/products/types';
import { requestRecommendedProducts } from 'src/redux/richRelevance/thunks';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { getStore, useStore } from 'src/redux/store';
import { fetchGlobalContent } from 'src/services/contentstack';
import * as redirection from 'src/services/redirection';
import * as styles from 'src/styles/pages.styles';
import { PageType } from 'src/types/navigation';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';
import { getFeatureFlagsWithMatchers } from 'src/utils/getFeatureFlagsWithMatchers';
import { getProductCodesFromPlacementItem } from 'src/utils/recommendations';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

const mapStateToProps = (state: ReduxApplicationState) => ({
  products: state.products,
  pageLiveRegionText: state.accessibility.statusMessage,
});

const connector = connect(mapStateToProps);

type SearchProductsPageProps = ProductsState & PageProps & ConnectedProps<typeof connector>;

const SearchResultsProductsPage = ({
  functionalCookiesConsented,
  pageLiveRegionText,
  products: { items, searchTerm },
  updatedReduxState,
}: SearchProductsPageProps) => {
  const { dispatch } = useStore();

  useFilterNavType((navType) => dispatch(navigationActions.setInitialPageLoad(navType)));

  useEffect(() => {
    getUserAttributes()
      .then((attributes) =>
        recommendations.initRecommendations({
          apiKey: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
          env: isProdLike() ? 'production' : 'qa',
          hasDisabledCookies: !functionalCookiesConsented,
          sessionId: updatedReduxState?.userConfiguration?.richRelevanceSessionId,
          userId: attributes?.sub,
        }),
      )
      .then(async () => {
        recommendations.setSearchResultsPage(searchTerm);

        if (items[0]) {
          const productCodes = getProductCodesFromPlacementItem(items[0]);

          recommendations.setSearchResultsProductCodes(productCodes);
        }

        const placementData = await recommendations.getRecommendations([
          Placement.SearchListingsRecentlyViewed,
        ]);

        const recommendedProductsRequests = placementData.map((placement) =>
          dispatch(requestRecommendedProducts(placement)),
        );

        await Promise.allSettled(recommendedProductsRequests);
        addRUMTiming('recommendationsLoaded');
      })
      .catch((e) => {
        console.warn(`Could not load recommendations on search listings page: ${e.message}`);
      });
  });

  return (
    <>
      <Head>
        {/* In browsers title Elements can only have a single Text Node as children */}
        <title>{`Search results for ${searchTerm}`}</title>
        <meta content="noindex" name="robots" />
      </Head>
      <ListingsApiErrorGuardContainer>
        <NoSearchResultsGuardContainer>
          <div css={visuallyHidden} role="status">
            {pageLiveRegionText}
          </div>
          <div css={styles.wrapper}>
            <div css={maxWidthContainer}>
              <SearchHeader searchTerm={searchTerm} />
            </div>
            <div id={elementIds.PRODUCT_CONTAINER_WRAPPER}>
              <SlimFilterMenuContainer />
              <div css={styles.quickFiltersWrapper}>
                <QuickFiltersContainer />
              </div>
              <div css={[grid, maxWidthContainer, styles.productListWrapper]}>
                <div css={styles.productListSidebar}>
                  <FilterContainer />
                </div>
                <div css={styles.productPagesWrapper}>
                  <ProductPagesContainer />
                  <RecommendedProductsShelfContainer
                    placementType={Placement.CategoryListingsRecentlyViewed}
                  />
                  <MembershipBannerSection
                    buttonHref="/membership"
                    buttonText="Tell me more"
                    copy="Then join our exclusive membership, <strong>NOTHS & More</strong>, for 10% off every order for a whole year."
                    heading="Want 10% off everything you see here?"
                    trackingLocation="search"
                  />
                  <BackToTopButton />
                </div>
              </div>
            </div>
          </div>
        </NoSearchResultsGuardContainer>
      </ListingsApiErrorGuardContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query, req, res }) => {
  const { term, ...queryParams } = query;
  const requestIsLoadTest = req.headers['use-webclient'] === 'true';

  if (term) {
    const pathname = parse(req.url!).pathname!;
    const {
      _ga: googleAnalyticsVisitorId,
      Loop54User,
      cognitoId,
      currency,
      delivery_zone,
      rr_session,
    } = req.cookies;
    const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
      parseLocaleSetCookieHeaders(res.getHeaders());
    const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
    const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);
    const { dispatch, getState } = getStore(
      {
        router: { pathname, query: queryParams },
      },
      {
        navigation: {
          pageType: PageType.Search,
        },
        user: {
          cognitoID: cognitoId || null,
        },
        userConfiguration: {
          currencyCode,
          gaVisitorId,
        },
      },
    );
    const functionalCookiesConsented = acceptsFunctionalCookies(req.cookies as CookieObj);
    const richRelevanceSessionId = getOrSetRichRelevanceSessionId(res, rr_session);
    const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};

    batch(() => {
      dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
      dispatch(userConfigurationActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
      dispatch(userConfigurationActions.setFeatureFlags(featureFlags));
      dispatch(userConfigurationActions.setRichRelevanceSessionId(richRelevanceSessionId));

      if (functionalCookiesConsented) {
        const loop54UserId = getOrSetLoop54UserId(res, Loop54User);
        dispatch(userConfigurationActions.setLoop54UserId(loop54UserId));
      }
    });

    const urlPath = req.url;
    const [, [navData, topBannerData]] = await Promise.all([
      dispatch(
        requestProducts({
          query,
          requestIsLoadTest,
        }),
      ),
      fetchGlobalContent({ featureFlags, urlPath }),
    ]);

    const state = getState();
    const { products } = state;

    if (products.redirectUrl) {
      return {
        redirect: {
          destination: products.redirectUrl,
          statusCode: StatusCode.MovedPermanently,
        },
      };
    }

    const redirect = redirection.productGuard(products);

    if (redirect) {
      return redirect;
    }

    if (products.error) {
      res.statusCode = StatusCode.InternalServerError;
      res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);

      const props: PageProps = {
        functionalCookiesConsented,
        navData,
        topBannerData,
        updatedReduxState: {
          // Cherry pick state to be updated on the client
          products,
          navigation: { pageType: PageType.Search },
        },
      };

      return {
        props,
      };
    }

    const props: PageProps = {
      featureFlags,
      navData,
      functionalCookiesConsented,
      topBannerData,
      ...selectPagePropsForClientHydration(state),
    };

    return {
      props,
    };
  } else {
    /*
      Commented out as part of interim solution for https://notonthehighstreet.atlassian.net/browse/FFA-961,
      to trigger redirect when empty search term is provided.
      Uncomment when appropriate solution is being implemented.
    */
    /* const props: PageProps = {
      updatedReduxState: {
        products: {
          error: {
            message: 'An unknown error occurred',
          },
        },
      },
    };

    return {
      props,
    }; */

    return {
      redirect: {
        destination: publicRuntimeConfig.hosts.search,
        statusCode: StatusCode.MovedPermanently,
      },
    };
  }
};

export default connector(SearchResultsProductsPage);
